import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import classNames from "classnames";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useDebounceSearch } from "../../customHooks/useDebouncedSearch";
import { useModalControl } from "../../customHooks/useModalControl";
import { useWindowDimensions } from "../../customHooks/useWindowDimensions";
import { Icon } from "../icons/icon";
import { TextInput, TextInputProps } from "../inputs/textInput";
import { PageLoader } from "../loaders/pageLoader";
import { ModalComponent } from "../modals";
import { SearchInput } from "./searchInput";

// const handleSearch = (searchFunction: (val: any) => void, query: any) => {
//   searchFunction(query);
// };

// const handleDebouncedSearch = debounce(handleSearch, 800);

const ComboBox = ({
  menuItems,
  className = "",
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  onSearch = () => {},
  onMenuClose = () => {},
  onSelectOption = () => {},
  loading = false,
  useInputLoader = false,
  immediate = false,
  showEmptyState = true,
  selected,
  customMenuItems = undefined,
  ...props
}: {
  menuItems?: {
    name: ReactNode;
    value?: string | object | number;
  }[];
  className?: string;
  menuClassName?: string;
  menuItemsClassName?: string;
  onSearch?: (val: string) => void;
  onMenuClose?: () => void;
  onSelectOption?: (val?: any) => void;
  loading?: boolean;
  useInputLoader?: boolean;
  immediate?: boolean;
  showEmptyState?: boolean;
  position?: AnchorProps;
  selected?: any;
  customMenuItems?: ReactNode | ((props: { open: boolean }) => ReactNode);
} & TextInputProps) => {
  const [searchVal, setSearchVal] = useState<string>();
  const [width, setWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useDebounceSearch({
    searchQuery: searchVal,
    onSearch: (query) => {
      onSearch(query as string);
    },
  });

  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const { width: windowWidth } = useWindowDimensions();

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef?.current?.offsetWidth]);

  useEffect(() => {
    setSearchVal(props.value);
  }, [props.value]);

  return windowWidth >= 768 ? (
    <>
      <Combobox
        as="div"
        className={`relative inline-block text-left !outline-none focus-within:!outline-none focus:!outline-none ${className}`}
        onChange={(val) => {
          if (val) {
            onSelectOption(val);
            setSearchVal("");
          }
        }}
        onClose={() => {
          onMenuClose();
        }}
        immediate={immediate}
      >
        {({ open }) => {
          return (
            <>
              <div
                className="w-full outline-none focus-within:outline-none focus:outline-none"
                ref={containerRef}
              >
                <ComboboxInput
                  as={TextInput}
                  autoFocus={false}
                  {...props}
                  loading={useInputLoader ? loading : false}
                />
              </div>

              {!showEmptyState && menuItems?.length === 0 ? undefined : (
                <Options
                  menuItems={menuItems}
                  menuClassName={menuClassName}
                  menuItemsClassName={menuItemsClassName}
                  position={position}
                  loading={loading}
                  showEmptyState={showEmptyState}
                  selected={selected}
                  width={width}
                  open={open}
                  customMenuItems={customMenuItems}
                />
              )}
            </>
          );
        }}
      </Combobox>
    </>
  ) : (
    <>
      <TextInput
        autoFocus={false}
        {...props}
        onChange={() => {}}
        readOnly
        onClick={(e) => {
          e.stopPropagation();

          if (windowWidth < 768) {
            e.preventDefault();
            handleModalOpen("popOverMenuModal");
          }
        }}
        loading={useInputLoader ? loading : false}
      />

      <ModalComponent
        isModalOpen={modalOpen && modal === "popOverMenuModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        hasButtons={false}
        showMobileTopBar
        showHeader={false}
        closeOnOutsideClick
        isMenuModal={false}
        modalClassName="!px-0"
      >
        <div className="flex w-full flex-col">
          <div className="bg-white px-4 pb-4 sticky top-0">
            <SearchInput
              placeholder="Search"
              searchFunction={(val) => {
                setSearchVal(val ?? "");
              }}
              className="w-full md:max-w-[286px] !px-4 !py-2"
              iconDimension={16}
            />
          </div>

          {menuItems?.map((opt, idx) => {
            return (
              <button
                key={`option-${idx}`}
                type="button"
                className="w-full text-left flex flex-row gap-2 items-center text-sm font-medium py-3 border-b last:border-b-0 px-4 md:hover:bg-light-green-bg-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectOption(opt.value);
                  setSearchVal("");
                  handleModalClose();
                }}
              >
                <span>{opt.name}</span>
              </button>
            );
          })}

          {menuItems && menuItems.length === 0 && !loading && showEmptyState ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
              <Icon
                svg="search-x"
                width={44}
                height={44}
                className="rounded-full bg-grey-100 p-3 text-grey-600"
              />

              <p className="text-sm">No Result Found</p>
            </div>
          ) : undefined}
        </div>
      </ModalComponent>
    </>
  );
};

const Options = ({
  menuItems,
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  loading = false,
  showEmptyState = true,
  selected,
  width,
  open,
  customMenuItems = undefined,
}: {
  menuItems?: {
    name: ReactNode;
    value?: string | object | number;
  }[];
  menuClassName?: string;
  menuItemsClassName?: string;
  loading?: boolean;
  showEmptyState?: boolean;
  position?: AnchorProps;
  selected?: any;
  width: number;
  open: boolean;
  customMenuItems?: ReactNode | ((props: { open: boolean }) => ReactNode);
}) => {
  return (
    <ComboboxOptions
      className={classNames(
        `min-w-ma relative !z-[900] flex w-full flex-col overflow-x-hidden overflow-y-scroll`,
        { "rounded-md border bg-white": customMenuItems === undefined },
        "[--anchor-gap:16px]",
        "-ml-[17px]",
        "outline-none focus-within:outline-none focus:outline-none",
        menuClassName
      )}
      transition
      anchor={position}
      style={{
        width: width,
        maxWidth: `${width}px`,
      }}
      portal
    >
      {!loading ? (
        <>
          {customMenuItems !== undefined ? (
            <div onClick={undefined}>
              {typeof customMenuItems === "function"
                ? customMenuItems({ open })
                : customMenuItems}
            </div>
          ) : (
            menuItems?.map((item, index) => {
              const key = `menu-item-${index}`;
              const isSelected =
                selected &&
                JSON.stringify(item.value) === JSON.stringify(selected);

              return (
                <ComboboxOption
                  key={key}
                  as="button"
                  type="button"
                  value={item.value}
                  className={classNames(
                    "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
                    menuItemsClassName
                  )}
                  data-selected={isSelected}
                  data-focus={isSelected}
                >
                  {item.name}
                </ComboboxOption>
              );
            })
          )}
        </>
      ) : undefined}

      {loading ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
          <PageLoader
            className="!min-h-fit"
            variant="spinner"
            width={20}
            height={20}
          />
        </div>
      ) : undefined}

      {menuItems && menuItems.length === 0 && !loading && showEmptyState ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
          <Icon
            svg="search-x"
            width={44}
            height={44}
            className="rounded-full bg-grey-100 p-3 text-grey-600"
          />

          <p className="text-sm">No Result Found</p>
        </div>
      ) : undefined}
    </ComboboxOptions>
  );
};

export default ComboBox;
